import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Modal } from '../components/Modal.tsx';
import type { Json } from '../lib/types.ts';

type Visitor = {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  company_name: string | null;
  host_name: string | null;
  purpose: string | null;
  site: string | null;
  status: 'on_site' | 'signed_out';
  registered_at: string;
  signed_out_at: string | null;
};

type VisitorAnswer = { id: string; question_label: string; answer: Json };
type VisitorDetail = Visitor & { visitor_answers: VisitorAnswer[] };

export function AdminVisitors() {
  const [tab, setTab] = useState<'on_site' | 'all'>('on_site');
  const [openId, setOpenId] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['visitors', tab],
    queryFn: () =>
      adminApi.get<{ visitors: Visitor[] }>('/api/visitors', {
        query: tab === 'on_site' ? { status: 'on_site' } : {},
      }),
  });

  const visitors = data?.visitors ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Visitors</h1>
        <p className="mt-1 text-sm text-slate-500">
          Who is on site. Visitors self-register at <code>/visit</code> and sign out when they
          leave.
        </p>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {(['on_site', 'all'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`border-b-2 px-3 py-2 text-sm transition ${
              tab === t
                ? 'border-blue-600 font-semibold text-blue-700'
                : 'border-transparent font-medium text-slate-500 hover:text-slate-700'
            }`}
          >
            {t === 'on_site' ? 'On site now' : 'All visits'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <CenteredSpinner />
      ) : visitors.length === 0 ? (
        <EmptyState title="No visitors" description="Nobody is registered on site right now." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">From</th>
                <th className="px-4 py-3">Host</th>
                <th className="px-4 py-3">Registered</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visitors.map((v) => (
                <tr key={v.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpenId(v.id)}>
                  <td className="px-4 py-3 font-medium text-slate-900">{v.name}</td>
                  <td className="px-4 py-3 text-slate-700">{v.company_name ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-700">{v.host_name ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-700">{new Date(v.registered_at).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        v.status === 'on_site'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {v.status === 'on_site' ? 'on site' : 'signed out'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId && <VisitorModal id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function VisitorModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['visitor', id],
    queryFn: () => adminApi.get<{ visitor: VisitorDetail }>(`/api/visitors/${id}`),
  });
  const v = data?.visitor;
  return (
    <Modal open onClose={onClose} title={v?.name ?? 'Visitor'}>
      {isLoading || !v ? (
        <CenteredSpinner />
      ) : (
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-2 text-slate-700">
            <div><span className="text-slate-400">From:</span> {v.company_name ?? '—'}</div>
            <div><span className="text-slate-400">Host:</span> {v.host_name ?? '—'}</div>
            <div><span className="text-slate-400">Phone:</span> {v.phone ?? '—'}</div>
            <div><span className="text-slate-400">Site:</span> {v.site ?? '—'}</div>
            <div className="col-span-2"><span className="text-slate-400">Purpose:</span> {v.purpose ?? '—'}</div>
          </div>
          {v.visitor_answers.length > 0 && (
            <div>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Responses
              </h3>
              <div className="space-y-1">
                {v.visitor_answers.map((a) => (
                  <div key={a.id} className="text-slate-700">
                    <span className="text-slate-500">{a.question_label}:</span>{' '}
                    {formatAnswer(a.answer)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function formatAnswer(a: Json): string {
  if (a === null || a === undefined) return '—';
  if (typeof a === 'boolean') return a ? 'Yes' : 'No';
  if (Array.isArray(a)) return a.join(', ');
  return String(a);
}
